import { NgClass, DecimalPipe } from '@angular/common';
import {
  Component,
  inject,
  OnInit,
  OnDestroy,
  signal,
  WritableSignal,
  computed,
  Injector,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import {
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { NotificationEmptyComponent } from '@next/core-lib/components';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { TranslatePipe } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { SettingsService, SettingsStorage } from '@next/core-lib/settings';
import {
  TableComponent,
  TableFiltersWrapperTemplateDirective,
  TableTopBarTemplateDirective,
} from '@next/core-lib/table-v2';
import { Store } from '@ngrx/store';
import { Subject, takeUntil, filter, timeout, catchError, of } from 'rxjs';

import { InseminationPrintComponent } from './print-view/insemination-print.component';
import { ThresholdSettingsDialogComponent } from './threshold-settings-dialog/threshold-settings-dialog.component';
import { InseminationDataSource } from '../../datasource/insemination.datasource';
import { AlvaMetricsDto } from '../../models/alva-metrics.model';

import {
  InseminationAdviceDto,
  InseminationAdviceStatus,
} from '../../models/insemination-advice.model';
import { ThresholdParams } from '../../models/threshold-params.model';
import { InseminationAdviceService } from '../../services/insemination-advice.service';

type SortColumn = 'earNumber' | 'age' | null;
type SortDirection = 'asc' | 'desc' | null;
const THRESHOLD_SETTINGS_KEY = 'insemination-advice-thresholds';
const SETTINGS_CONTEXT_NAME = 'DairyStartMonitor';

@Component({
  selector: 'insemination-overview',
  templateUrl: './insemination-overview.component.html',
  styleUrls: ['./insemination-overview.component.scss'],
  imports: [
    MatButton,
    MatIcon,
    MatProgressSpinner,
    NotificationEmptyComponent,
    TableComponent,
    TableFiltersWrapperTemplateDirective,
    TableTopBarTemplateDirective,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    NgClass,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    InseminationPrintComponent,
    DecimalPipe,
    TranslatePipe,
  ],
})
export class InseminationOverviewComponent implements OnInit, OnDestroy {
  private readonly inseminationService = inject(InseminationAdviceService);
  private readonly injector = inject(Injector);
  private readonly store = inject(Store);
  private readonly translatePipe = inject(TranslatePipe);
  private readonly dialogService = inject(DialogService);
  private readonly settingsService = inject(SettingsService);
  private readonly destroy$ = new Subject<void>();
  private readonly defaultThresholds: ThresholdParams = {
    minAgeWithoutMeasurement: 15,
    minAgeWithMeasurement: 12,
    minimumHeightWithMeasurement: 135,
  };
  private readonly useCustomThresholds = signal(false);

  protected minAgeWithoutMeasurement = signal(15);
  protected minAgeWithMeasurement = signal(12);
  protected minimumHeightWithMeasurement = signal(135);

  protected thresholds = computed<ThresholdParams>(() => ({
    minAgeWithoutMeasurement: this.minAgeWithoutMeasurement(),
    minAgeWithMeasurement: this.minAgeWithMeasurement(),
    minimumHeightWithMeasurement: this.minimumHeightWithMeasurement(),
  }));
  protected thresholdsForApi = computed<ThresholdParams | null>(() =>
    this.useCustomThresholds() ? this.thresholds() : null,
  );

  protected advice: WritableSignal<InseminationAdviceDto[]> = signal([]);
  protected sortColumn: WritableSignal<SortColumn> = signal(null);
  protected sortDirection: WritableSignal<SortDirection> = signal(null);
  protected isLoading = signal(false);
  protected editingDates: Map<string, Date | null> = new Map();
  protected printTableData: import('../../models/insemination-advice.model').InseminationPrintRow[] =
    [];
  protected measurementDateForPrint: Date | null = null;
  protected alvaMetrics = signal<AlvaMetricsDto | null>(null);

  protected readonly displayColumns = [
    'warning',
    'animalId',
    'earNumber',
    'name',
    'age',
    'height',
    'estimatedHeight',
    'lastInsemination',
    'advice',
  ];

  protected inseminationDataSource: InseminationDataSource;

  protected readonly InseminationAdviceStatus = InseminationAdviceStatus;

  ngOnInit(): void {
    this.settingsService
      .getSingle<ThresholdParams>(
        {
          contextName: SETTINGS_CONTEXT_NAME,
          key: THRESHOLD_SETTINGS_KEY,
          storage: SettingsStorage.Global,
          basedOnCurrentLocation: true,
        },
        this.defaultThresholds,
      )
      .pipe(
        timeout(5000),
        catchError((error) => {
          console.error('Failed to load threshold settings:', error);
          this.store.dispatch(
            NotificationActions.warningSnackbar({
              message: this.translatePipe.transform(
                'insemination-advice.error.settings-load-failed',
              ),
            }),
          );
          return of(this.defaultThresholds);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((thresholds) => {
        this.minAgeWithoutMeasurement.set(thresholds.minAgeWithoutMeasurement);
        this.minAgeWithMeasurement.set(thresholds.minAgeWithMeasurement);
        this.minimumHeightWithMeasurement.set(thresholds.minimumHeightWithMeasurement);
        this.useCustomThresholds.set(!this.isDefaultThresholds(thresholds));

        this.initializeDataSource();
      });
  }

  private initializeDataSource(): void {
    this.inseminationDataSource = new InseminationDataSource(
      this.inseminationService,
      this.thresholdsForApi,
      this.injector,
    );
    this.inseminationDataSource
      .connect(null)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.advice.set(Array.from(data));
        },
        error: (error) => {
          console.error('Error loading insemination advice:', error);
          this.store.dispatch(
            NotificationActions.errorSnackbar({
              message: this.translatePipe.transform('insemination-advice.error.load-failed'),
            }),
          );
        },
      });

    this.inseminationDataSource.loading$.pipe(takeUntil(this.destroy$)).subscribe((v) => {
      this.isLoading.set(v);
    });

    this.inseminationService
      .getAlvaMetrics(this.minimumHeightWithMeasurement())
      .pipe(
        catchError((error) => {
          console.error('Failed to load ALVA metrics:', error);
          return of(null);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((metrics) => {
        queueMicrotask(() => this.alvaMetrics.set(metrics));
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private isDefaultThresholds(values: ThresholdParams): boolean {
    return (
      values.minAgeWithoutMeasurement === this.defaultThresholds.minAgeWithoutMeasurement &&
      values.minAgeWithMeasurement === this.defaultThresholds.minAgeWithMeasurement &&
      values.minimumHeightWithMeasurement === this.defaultThresholds.minimumHeightWithMeasurement
    );
  }

  protected getRowClass(cow: InseminationAdviceDto): string {
    switch (cow.status) {
      case InseminationAdviceStatus.InseminateAsap:
        return 'row-inseminate-asap';
      case InseminationAdviceStatus.Inseminate:
        return 'row-inseminate';
      case InseminationAdviceStatus.AlreadyInseminated:
        return 'row-already-inseminated';
      default:
        return '';
    }
  }

  protected shouldShowWarning(cow: InseminationAdviceDto): boolean {
    return cow.status === InseminationAdviceStatus.InseminateAsap;
  }

  protected getAdviceText(cow: InseminationAdviceDto): string {
    switch (cow.status) {
      case InseminationAdviceStatus.InseminateAsap:
        return this.translatePipe.transform('insemination-advice.status.inseminate-asap');
      case InseminationAdviceStatus.Inseminate:
        return this.translatePipe.transform('insemination-advice.status.inseminate');
      case InseminationAdviceStatus.InseminateInXDays:
        if (cow.predictedInseminationDate) {
          const template = this.translatePipe.transform(
            'insemination-advice.status.inseminate-in-days',
          );
          return template.replace('{{date}}', this.formatLocaleDate(cow.predictedInseminationDate));
        }
        return '';
      case InseminationAdviceStatus.AlreadyInseminated:
        if (cow.dateOfLastInsemination) {
          const template = this.translatePipe.transform(
            'insemination-advice.status.already-inseminated',
          );
          return template.replace('{{date}}', this.formatLocaleDate(cow.dateOfLastInsemination));
        }
        return this.translatePipe.transform('insemination-advice.status.already-inseminated');
      default:
        return '';
    }
  }

  protected parseDate(dateString: string | undefined): Date | null {
    return dateString ? new Date(dateString) : null;
  }

  protected formatDate(dateString: string | undefined): string {
    if (!dateString) {
      return '';
    }
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  protected getTotalAnimalCount(): number {
    return this.advice().length;
  }

  private formatLocaleDate(dateString: string): string {
    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  protected sortData(column: SortColumn): void {
    if (this.sortColumn() === column) {
      // Toggle direction: asc -> desc -> null
      if (this.sortDirection() === 'asc') {
        this.sortDirection.set('desc');
      } else if (this.sortDirection() === 'desc') {
        this.sortDirection.set(null);
        this.sortColumn.set(null);
      }
    } else {
      this.sortColumn.set(column);
      this.sortDirection.set('asc');
    }

    this.inseminationDataSource.setSorting(this.sortColumn(), this.sortDirection());
  }

  protected refreshData(): void {
    this.inseminationDataSource.applySorting();
  }

  protected printReport(): void {
    const rows = this.advice();
    this.printTableData = rows.map((r) => {
      const className = this.getRowClass(r);

      return {
        number: r.registrationNumber,
        earNumber: r.earNumber,
        name: r.name,
        ageInMonths: r.ageInMonths,
        hipHeight: r.heightCm,
        estimatedHipHeight: r.estimatedHeightCm,
        lastInsemination: r.dateOfLastInsemination ? new Date(r.dateOfLastInsemination) : null,
        advice: this.getAdviceText(r),
        status: r.status,
        className,
      };
    });

    this.measurementDateForPrint = new Date();

    const originalTitle = document.title;
    const date = new Date().toLocaleDateString('nl-NL').split('/').join('-');
    document.title = `Inseminatierapport_${date}`;

    setTimeout(() => {
      globalThis.print();
      document.title = originalTitle;
    }, 50);
  }

  protected openThresholdSettingsDialog(): void {
    const currentValues = {
      minAgeWithoutMeasurement: this.minAgeWithoutMeasurement(),
      minAgeWithMeasurement: this.minAgeWithMeasurement(),
      minimumHeightWithMeasurement: this.minimumHeightWithMeasurement(),
    };

    this.dialogService
      .form(ThresholdSettingsDialogComponent, currentValues)
      .pipe(
        filter(({ status }) => status === DIALOG_RETURN_TYPES.confirm),
        takeUntil(this.destroy$),
      )
      .subscribe(({ result }) => {
        this.minAgeWithoutMeasurement.set(result.minAgeWithoutMeasurement);
        this.minAgeWithMeasurement.set(result.minAgeWithMeasurement);
        this.minimumHeightWithMeasurement.set(result.minimumHeightWithMeasurement);
        this.useCustomThresholds.set(!this.isDefaultThresholds(result));

        this.inseminationService
          .getAlvaMetrics(result.minimumHeightWithMeasurement)
          .pipe(takeUntil(this.destroy$))
          .subscribe((metrics) => {
            this.alvaMetrics.set(metrics);
          });

        this.settingsService
          .setSettings({
            contextName: SETTINGS_CONTEXT_NAME,
            key: THRESHOLD_SETTINGS_KEY,
            storage: SettingsStorage.Global,
            basedOnCurrentLocation: true,
            data: { ...result },
          })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {},
            error: (error) => {
              console.error('Failed to save threshold settings:', error);
              this.store.dispatch(
                NotificationActions.warningSnackbar({
                  message: this.translatePipe.transform(
                    'insemination-advice.error.save-failed-temp',
                  ),
                  duration: '0',
                }),
              );
            },
          });
      });
  }
}
